import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ' +
    'text-sm font-medium transition-surface disabled:pointer-events-none ' +
    'disabled:opacity-50 select-none',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-content hover:bg-accent-hover',
        secondary:
          'bg-surface-raised text-content border border-border hover:bg-surface-hover',
        ghost: 'text-content-muted hover:bg-surface-hover hover:text-content',
        danger: 'text-danger hover:bg-danger-subtle',
        link: 'text-accent underline-offset-4 hover:underline',
      },
      size: {
        // Desktop sizes first; below `sm` every size grows to a target a
        // thumb lands on (44px is the floor Apple and Google both publish).
        // Done here rather than per call site, so no button is forgotten.
        sm: 'h-7 px-2.5 text-xs max-sm:h-11 max-sm:px-4 max-sm:text-sm touch:h-11 touch:px-4 touch:text-sm',
        md: 'h-9 px-3.5 max-sm:h-11 max-sm:px-4 touch:h-11 touch:px-4',
        lg: 'h-10 px-5 max-sm:h-12 touch:h-12',
        icon: 'h-8 w-8 max-sm:h-11 max-sm:w-11 touch:h-11 touch:w-11',
        'icon-sm': 'h-6 w-6',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Render as the child element instead of a `<button>` (e.g. a link). */
  asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot : 'button';
  return (
    <Component
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
